import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridActionsCellItem, GridRowModes, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';

import { clearEditMode, dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, AddButtonUi, DatagridPremiumUi, OrderArrowsUI, InputUI, FormErrorUI } from '../Interface';

const EditToolbar = (props) => {
  const { push, defaultField, jokerValues, valueIndex } = props;
  const { validateField } = useFormikContext();

  const handleClick = async () => {
    const index = jokerValues.length;

    const maxVisOrder = Math.max(...(jokerValues.map((item) => item.visOrder) ?? []), 0);

    const newField = {
      ...defaultField,
      id: uuidv4(),
      visOrder: maxVisOrder + 1,
      isNew: true,
    };

    await push(newField);

    const fieldsToValidate = [
      `fields.${valueIndex}.jokerValues.${index}.value`,
      `fields.${valueIndex}.jokerValues.${index}.label`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const JokerSelectListTable = ({ defaultField, valueIndex }) => {
  const apiRef = useGridApiRef();
  const { values, setFieldValue, errors, validateField } = useFormikContext();

  const jokerValues = values.fields?.[valueIndex]?.jokerValues ?? [];

  const getValueIndex = (id) => jokerValues.findIndex((rc) => rc.id === id);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'visOrder', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    if (newOrder < 1 || newOrder > jokerValues.length) {
      return;
    }

    const updatedList = jokerValues
      .map((item) => {
        if (item.id === id) {
          return { ...item, visOrder: newOrder };
        } else if (item.visOrder === newOrder) {
          return { ...item, visOrder: currentOrder };
        }
        return item;
      })
      .sort((a, b) => a.visOrder - b.visOrder);

    setFieldValue(`fields.${valueIndex}.jokerValues`, updatedList);
  };

  const columns = (remove) => {
    return dataGridPremiumColumns([
      {
        field: 'visOrder',
        type: 'string',
        headerName: 'Sorrend',
        renderCell: (params) => {
          const handleUpClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.id, params.row.visOrder, 'up');
          };
          const handleDownClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.id, params.row.visOrder, 'down');
          };
          return (
            <Box display="flex" alignItems="center">
              <Box mr={1}>{params.row.visOrder}</Box>
              <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} />
            </Box>
          );
        },
      },
      {
        field: 'value',
        headerName: 'Érték',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const rowIndex = getValueIndex(data.id);

          return (
            <Field
              name={`fields.${valueIndex}.jokerValues.${rowIndex}.value`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.fields?.[valueIndex]?.jokerValues?.[rowIndex]?.value} />}
              onChange={(e) => {
                setFieldValue(`fields.${valueIndex}.jokerValues.${rowIndex}.value`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={() => validateField(`fields.${valueIndex}.jokerValues.${rowIndex}.value`)}
            />
          );
        },
      },
      {
        field: 'label',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const rowIndex = getValueIndex(data.id);

          return (
            <Field
              name={`fields.${valueIndex}.jokerValues.${rowIndex}.label`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.fields?.[valueIndex]?.jokerValues?.[rowIndex]?.label} />}
              onChange={(e) => {
                setFieldValue(`fields.${valueIndex}.jokerValues.${rowIndex}.label`, e.target.value ?? '');

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={() => validateField(`fields.${valueIndex}.jokerValues.${rowIndex}.label`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        width: 60,
        getActions: ({ id, row }) => {
          if (!row?.isNew) return [];
          const valueIndex = getValueIndex(values, id);

          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Törlés"
              onClick={() => remove(valueIndex)}
              showInMenu={false}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto mb-2">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name={`fields.${valueIndex}.jokerValues`}>
            {({ push, remove }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.JokerSelectListTable}
                height={480}
                datagridInitialState={initialState}
                columns={columns(remove)}
                rows={jokerValues}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    push,
                    defaultField,
                    valueIndex,
                    jokerValues,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default JokerSelectListTable;
